import React, { useState, useEffect, useRef } from "react";
import Modal from "../../shared/Modal";
import { Button } from "../../../ds2/components";
import { X, Code } from "lucide-react";
import { MentionsInput, Mention } from "react-mentions";
import { useSelector } from "react-redux";
import { selectEmployeeRecords, setEmployeeRecords } from "../../../redux/features/agent/agentSlice";
import { selectUser } from "../../../redux/features/appData";
import { useDispatch } from "react-redux";
import { apiGetEmployeeRecordsForWorkspace } from "../../../services/EmployeeRecordService";
import { erGetCPValue } from "../../app/app-employee-records/employeeRecordUtils";
import { getDefaultUserAvatar } from "winslow-core";
import { ValidatedImage } from "../../common/ValidatedImage";
import "./ModalVibeCodeBuilder.css";

type ModalVibeCodeBuilderProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (description: string, workflowPrompt: string) => void;
    onCancel: () => void;
};

const MentionsInputAny = MentionsInput as unknown as React.FC<any>;
const MentionAny = Mention as unknown as React.FC<any>;

export const ModalVibeCodeBuilder = ({
    isOpen,
    onClose,
    onSubmit,
    onCancel,
}: ModalVibeCodeBuilderProps) => {
    const dispatch = useDispatch();
    const storeUser = useSelector(selectUser);
    const employeeRecords = useSelector(selectEmployeeRecords);
    
    const [description, setDescription] = useState("");
    const [workflowPrompt, setWorkflowPrompt] = useState("");
    const [workflowPromptPlainText, setWorkflowPromptPlainText] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const workflowPromptRef = useRef<HTMLTextAreaElement | null>(null);
    
    // Load employee records when modal opens
    useEffect(() => {
        if (isOpen && storeUser?.currentWorkspaceId) {
            const loadEmployeeRecords = async () => {
                try {
                    const workspaceId = storeUser.currentWorkspaceId;
                    if (!workspaceId) return;
                    console.log('[ModalVibeCodeBuilder] Loading employee records for workspace:', workspaceId);
                    const records = await apiGetEmployeeRecordsForWorkspace(workspaceId);
                    console.log('[ModalVibeCodeBuilder] Loaded employee records:', records?.length || 0);
                    if (records && records.length > 0) {
                        dispatch(setEmployeeRecords(records));
                        console.log('[ModalVibeCodeBuilder] Dispatched employee records to Redux');
                    } else {
                        console.warn('[ModalVibeCodeBuilder] No employee records returned from API');
                    }
                } catch (error) {
                    console.error("Error loading employee records:", error);
                }
            };
            // Always try to load, in case records aren't in Redux yet
            if (!employeeRecords || employeeRecords.length === 0) {
                loadEmployeeRecords();
            } else {
                console.log('[ModalVibeCodeBuilder] Employee records already loaded:', employeeRecords.length);
            }
        }
    }, [isOpen, storeUser?.currentWorkspaceId, dispatch]); // Removed employeeRecords to prevent infinite loop
    
    // Get employee record mentions only
    const getEmployeeRecordMentions = (query: string = "") => {
        if (!employeeRecords || employeeRecords.length === 0) return [];
        
        const lowercaseQuery = query.toLowerCase().trim();
        
        // If query is empty, return all employee records (limit to 8)
        let filtered = employeeRecords;
        if (lowercaseQuery) {
            filtered = employeeRecords.filter((er) => {
                const firstName = erGetCPValue(er, "system-firstName") || "";
                const lastName = erGetCPValue(er, "system-lastName") || "";
                const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
                const email = er.workEmail || "";
                return (
                    displayName.toLowerCase().includes(lowercaseQuery) ||
                    email.toLowerCase().includes(lowercaseQuery)
                );
            });
        }
        
        return filtered.slice(0, 8).map((er) => {
            const firstName = erGetCPValue(er, "system-firstName") || "";
            const lastName = erGetCPValue(er, "system-lastName") || "";
            const displayName = [firstName, lastName].filter(Boolean).join(" ").trim() || er.workEmail || "Employee";
            return {
                id: `employee-record-${er.id}`,
                display: displayName,
            };
        });
    };
    
    const renderEmployeeRecordSuggestion = (
        suggestion: any,
        search: string,
        highlightedDisplay: React.ReactNode,
        index: number,
        focused: boolean
    ) => {
        const recordId = suggestion.id.replace("employee-record-", "");
        const employeeRecord = employeeRecords?.find(er => er.id === recordId);
        
        if (!employeeRecord) return null;
        
        const firstName = erGetCPValue(employeeRecord, "system-firstName") || "";
        const lastName = erGetCPValue(employeeRecord, "system-lastName") || "";
        const displayName = [firstName, lastName].filter(Boolean).join(" ").trim() || employeeRecord.workEmail || "Employee";
        const fallbackAvatar = getDefaultUserAvatar(displayName || employeeRecord.workEmail || "");
        
        return (
            <div
                key={suggestion.id}
                className="mention-suggestion-option"
                style={{
                    padding: "8px 12px",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "#333",
                    backgroundColor: focused ? "#f8f8f8" : "transparent",
                    transition: "background-color 0.1s ease",
                }}>
                <ValidatedImage
                    imageUrl={employeeRecord.avatarURL || ""}
                    fallbackUrl={fallbackAvatar}
                    alt={displayName}
                    className="mention-suggestion-avatar"
                />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: "500" }}>{highlightedDisplay}</div>
                    {employeeRecord.workEmail && (
                        <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                            {employeeRecord.workEmail}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const handleSubmit = async () => {
        if (!description.trim() || !workflowPromptPlainText.trim()) {
            return;
        }
        
        setIsSubmitting(true);
        try {
            // workflowPrompt contains the encoded mentions (e.g., "@[John Doe](employee-record-uuid)")
            await onSubmit(description.trim(), workflowPrompt.trim());
            setDescription("");
            setWorkflowPrompt("");
            setWorkflowPromptPlainText("");
        } catch (error) {
            console.error("Error submitting description:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setDescription("");
        setWorkflowPrompt("");
        setWorkflowPromptPlainText("");
        onCancel();
    };

    const handleClose = () => {
        setDescription("");
        setWorkflowPrompt("");
        setWorkflowPromptPlainText("");
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            handleClose();
        }
    };

    // Debug: Log when component renders
    useEffect(() => {
        if (isOpen) {
            console.log('[ModalVibeCodeBuilder] Component rendered, isOpen:', isOpen, 'employeeRecords:', employeeRecords?.length || 0);
        }
    }, [isOpen, employeeRecords?.length]);

    if (!isOpen) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            canClose={true}
            allowClickout={false}
            style={null}
        >
            <div className="modal-vibe-code-builder" onKeyDown={handleKeyDown}>
                <div className="modal-vibe-code-builder-header">
                    <div className="modal-vibe-code-builder-title">
                        {/* <Code size={24} className="modal-vibe-code-builder-icon" /> */}
                        <h2>Extend Winslow's Capabilities</h2>
                    </div>
                    {/* <button
                        className="modal-vibe-code-builder-close"
                        onClick={handleClose}
                        aria-label="Close modal"
                    >
                        <X size={20} />
                    </button> */}
                </div>

                <div className="modal-vibe-code-builder-content">
                    <div className="modal-vibe-code-builder-input-section">
                        <label 
                            htmlFor="vibe-description" 
                            className="modal-vibe-code-builder-label"
                        >
                            What do you want to build?
                        </label>
                        <textarea
                            id="vibe-description"
                            className="modal-vibe-code-builder-textarea"
                            placeholder="Describe your project, feature, or idea in detail. Include any specific requirements, constraints, or preferences you have..."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={6}
                            maxLength={2000}
                        />
                        <div className="modal-vibe-code-builder-char-count">
                            {description.length}/2000 characters
                        </div>
                    </div>

                    <div className="modal-vibe-code-builder-input-section">
                        <label 
                            htmlFor="vibe-workflow-prompt" 
                            className="modal-vibe-code-builder-label"
                        >
                            Workflow Instructions
                        </label>
                        <MentionsInputAny
                            id="vibe-workflow-prompt"
                            className="mentions modal-vibe-code-builder-textarea-mentions"
                            value={workflowPrompt}
                            allowSuggestionsAboveCursor={true}
                            forceSuggestionsAboveCursor={true}
                            onChange={(
                                e: any,
                                newValue: string,
                                newPlainTextValue: string
                            ) => {
                                console.log('[ModalVibeCodeBuilder] onChange called, newValue:', newValue.substring(0, 50), 'plainText:', newPlainTextValue.substring(0, 50));
                                setWorkflowPrompt(newValue); // Store markup version with mentions
                                setWorkflowPromptPlainText(newPlainTextValue); // Store plain text for character count
                            }}
                            placeholder="Enter detailed instructions for how this workflow should operate, including specific steps, decision points, or automation rules..."
                            allowSpaceInQuery={false}
                            inputRef={(el: any) => {
                                workflowPromptRef.current = el as HTMLTextAreaElement | null;
                            }}
                            onKeyDown={(e: React.KeyboardEvent) => {
                                // Prevent Enter from submitting if suggestions are open
                                if (e.key === "Enter" && !e.shiftKey) {
                                    const suggestionsOpen = !!document.querySelector(
                                        ".mentions__suggestions__list"
                                    );
                                    if (suggestionsOpen) {
                                        e.preventDefault();
                                    }
                                }
                            }}
                            style={{
                                control: {
                                    fontSize: "inherit",
                                    fontWeight: "normal",
                                },
                                highlighter: {
                                    overflow: "hidden",
                                },
                                input: {
                                    margin: 0,
                                    outline: 0,
                                    border: 0,
                                    padding: "12px 16px",
                                },
                                "&multiLine": {
                                    control: {
                                        fontFamily: "inherit",
                                        minHeight: 100,
                                    },
                                    highlighter: {
                                        padding: "12px 16px",
                                        border: "none",
                                    },
                                    input: {
                                        padding: "12px 16px",
                                        border: "none",
                                        outline: 0,
                                    },
                                },
                                mention: {
                                    backgroundColor: "#2563eb",
                                    color: "#ffffff",
                                    border: "1px solid #1d4ed8",
                                    borderRadius: "16px",
                                    padding: "4px 8px",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    margin: "0 2px",
                                    lineHeight: "1.2",
                                    verticalAlign: "middle",
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                                    transition: "all 0.2s ease",
                                    textDecoration: "none",
                                },
                                suggestions: {
                                    list: {
                                        backgroundColor: "white",
                                        border: "1px solid #e0e0e0",
                                        fontSize: 14,
                                        borderRadius: 8,
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                        maxHeight: 200,
                                        overflowY: "auto",
                                        zIndex: 1002, // Higher than modal z-index (1000)
                                        minWidth: 250,
                                        // Let react-mentions handle positioning (allowSuggestionsAboveCursor + forceSuggestionsAboveCursor will position it above)
                                    },
                                    item: {
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "8px",
                                        cursor: "pointer",
                                        color: "var(--text-color-primary, #333)",
                                        gap: "8px",
                                        borderBottom: "none",
                                        "&focused": {
                                            backgroundColor: "#f0f0f0",
                                        },
                                    },
                                },
                            }}>
                            <MentionAny
                                trigger="@"
                                markup="@[__display__](__id__)"
                                appendSpaceOnAdd={true}
                                displayTransform={(id: string, display: string) =>
                                    `@${display}`
                                }
                                data={(query: string, callback: (results: Array<{ id: string; display: string }>) => void) => {
                                    console.log('[ModalVibeCodeBuilder] data function called with query:', query);
                                    console.log('[ModalVibeCodeBuilder] employeeRecords in Redux:', employeeRecords?.length || 0);
                                    // Ensure we always call the callback, even with empty results
                                    const results = getEmployeeRecordMentions(query);
                                    console.log('[ModalVibeCodeBuilder Mentions] Query:', query, 'Results:', results.length, 'Employee records available:', employeeRecords?.length || 0);
                                    console.log('[ModalVibeCodeBuilder Mentions] Sample results:', results.slice(0, 2));
                                    // react-mentions expects the callback to be called synchronously
                                    if (typeof callback === 'function') {
                                        console.log('[ModalVibeCodeBuilder] Calling callback with', results.length, 'results');
                                        callback(results);
                                    } else {
                                        console.error('[ModalVibeCodeBuilder] Callback is not a function:', typeof callback);
                                    }
                                }}
                                renderSuggestion={renderEmployeeRecordSuggestion}
                            />
                        </MentionsInputAny>
                        <div className="modal-vibe-code-builder-char-count">
                            {workflowPromptPlainText.length}/1000 characters
                        </div>
                    </div>
                </div>

                <div className="modal-vibe-code-builder-footer">
                    <div className="modal-vibe-code-builder-actions">
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={!description.trim() || !workflowPromptPlainText.trim() || isSubmitting}
                        >
                            {isSubmitting ? "Creating" : "Start Building"}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
